import { useState } from 'react'
import { useBoardState, useStore } from '../store/context'
import type { RefusedChange } from '../store/local'
import { toast } from './toasts'
import { sameHolder } from '../issue/IssueProperties'
import { senderName } from '../sessions/words'

export function copyOf(change: RefusedChange): string {
  const mutation = change.mutation
  if (mutation.op === 'update_issue' && mutation.args.description !== undefined) return mutation.args.description
  if (mutation.op === 'set_plan') return mutation.args.plan
  if (mutation.op === 'set_outcome') return mutation.args.outcome
  if (mutation.op === 'add_record') return mutation.args.body
  if (mutation.op === 'set_plan_body') return mutation.args.body
  if (mutation.op === 'add_plan_comment' || mutation.op === 'edit_plan_comment') return mutation.args.body
  return JSON.stringify(change.mutation, null, 2)
}

function nameOf(change: RefusedChange): string {
  switch (change.mutation.op) {
    case 'answer': return 'Answer question'
    case 'create_issue': return 'Create issue'
    case 'add_record': return 'Add comment'
    case 'update_issue': return 'Edit issue'
    case 'move_issue': return 'Move issue'
    case 'set_plan': return 'Save plan'
    case 'set_outcome': return 'Save outcome'
    case 'update_label': return 'Edit label'
    case 'delete_label': return 'Delete label'
    case 'add_label': return 'Add label'
    case 'remove_label': return 'Remove label'
    case 'relate_issues': return 'Add relation'
    case 'unrelate_issues': return 'Remove relation'
    case 'claim_issue': return 'Claim issue'
    case 'release_issue': return 'Release claim'
    case 'create_plan': return 'Create plan'
    case 'update_plan': return 'Edit plan'
    case 'set_plan_body':
    case 'patch_plan': return 'Save plan'
    case 'list_in_plan': return 'Add to plan'
    case 'unlist_from_plan': return 'Remove from plan'
    case 'move_in_plan': return 'Move in plan'
    case 'claim_plan': return 'Claim plan'
    case 'release_plan': return 'Release plan'
    case 'add_plan_comment': return 'Add comment'
    case 'edit_plan_comment': return 'Edit comment'
    default: return 'Change issue'
  }
}

/** Refused changes stay here until the person retries or discards them. */
export function NotSynced() {
  const { refused, issues, me } = useBoardState()
  // A claim refused because another holds the issue now: the first claim the server accepted won (Poulpe-826).
  const claimedFirst = (change: RefusedChange): string | null => {
    if (change.mutation.op !== 'claim_issue') return null
    const claim = issues.get(change.issueId)?.claim
    return claim && !sameHolder(claim.by, me?.actor) ? `${senderName(claim.by, me)} claimed it first` : null
  }
  const store = useStore()
  const [open, setOpen] = useState(false)
  if (!refused.length) return null
  return (
    <section className="section" aria-label="Not synced">
      <button type="button" className="button" aria-expanded={open} onClick={() => setOpen(!open)}>
        {`Not synced (${refused.length})`}
      </button>
      {open && <ul>
        {refused.map((change) => <li key={change.mutationId}>
          <strong>{change.issueKey}</strong>{' · '}{nameOf(change)}
          <p>{claimedFirst(change) ?? <>{change.message}{change.reason ? ` (${change.reason})` : ''}</>}</p>
          {change.detail !== undefined && <pre>{JSON.stringify(change.detail, null, 2)}</pre>}
          <button type="button" className="button" onClick={() => void store.retryRefused(change.mutationId)}>Retry</button>{' '}
          <button type="button" className="button" onClick={() => {
            void Promise.resolve().then(() => navigator.clipboard.writeText(copyOf(change))).catch(() => toast('Could not copy the change.', 'error'))
          }}>Copy</button>{' '}
          <button type="button" className="button" onClick={() => void store.discardRefused(change.mutationId)}>Discard</button>
        </li>)}
      </ul>}
    </section>
  )
}
